import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AlertCondition = 'above' | 'below' | 'golden' | 'death' | 'near_high' | 'near_low'
export type AlertMetric = 'price' | 'rsi' | 'volume' | 'rvol' | 'news' | 'change_pct' | 'macd_hist' | 'ema_cross' | 'ema_cross_golden' | 'ema_cross_death' | 'week52_pct'

export interface CompoundCondition {
  metric:    AlertMetric
  condition: AlertCondition
  threshold: number
}

export interface Alert {
  id:        string
  symbol:    string
  metric:    AlertMetric
  condition: AlertCondition
  threshold: number
  keyword?:  string          // for 'news' alerts
  active:    boolean
  triggered: boolean
  createdAt: string
  lastNewsCheck?: string     // ISO timestamp of last news check
  // Multi-factor / multi-timeframe extensions
  alertType?:          'simple' | 'compound'
  compoundConditions?: CompoundCondition[]
  compoundLogic?:      'AND' | 'OR'
  timeframe?:          '1d' | '1wk' | '1mo'
}

interface AlertState {
  alerts: Alert[]
  discordWebhookUrl:  string
  telegramBotToken:   string
  telegramChatId:     string
  notifPermission:    NotificationPermission | 'unsupported'
  addAlert:           (a: Omit<Alert, 'id' | 'createdAt' | 'triggered'>) => void
  removeAlert:        (id: string) => void
  toggleAlert:        (id: string) => void
  markTriggered:      (id: string) => void
  resetTriggered:     (id: string) => void
  setDiscordWebhook:  (url: string) => void
  setTelegramConfig:  (token: string, chatId: string) => void
  setNotifPermission: (p: NotificationPermission | 'unsupported') => void
  updateNewsCheck:    (id: string) => void
}

export const useAlertStore = create<AlertState>()(
  persist(
    (set) => ({
      alerts: [],
      discordWebhookUrl: '',
      telegramBotToken:  '',
      telegramChatId:    '',
      notifPermission:   (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported') as NotificationPermission | 'unsupported',

      addAlert: (a) => set((s) => ({
        alerts: [
          ...s.alerts,
          { ...a, id: crypto.randomUUID(), triggered: false, createdAt: new Date().toISOString(), lastNewsCheck: new Date().toISOString() },
        ],
      })),

      removeAlert: (id) => set((s) => ({ alerts: s.alerts.filter((a) => a.id !== id) })),

      toggleAlert: (id) => set((s) => ({
        alerts: s.alerts.map((a) => a.id === id ? { ...a, active: !a.active, triggered: false } : a),
      })),

      markTriggered: (id) => set((s) => ({
        alerts: s.alerts.map((a) => a.id === id ? { ...a, triggered: true } : a),
      })),

      resetTriggered: (id) => set((s) => ({
        alerts: s.alerts.map((a) => a.id === id ? { ...a, triggered: false } : a),
      })),

      setDiscordWebhook:  (url) => set({ discordWebhookUrl: url }),
      setTelegramConfig:  (token, chatId) => set({ telegramBotToken: token, telegramChatId: chatId }),
      setNotifPermission: (p) => set({ notifPermission: p }),
      updateNewsCheck: (id) => set((s) => ({
        alerts: s.alerts.map((a) => a.id === id ? { ...a, lastNewsCheck: new Date().toISOString() } : a),
      })),
    }),
    { name: 'bb-alerts' }
  )
)
